import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listRecordings, notReady, reasonOf, spokenLength, expiryOf, transcriptWords } from '../../lib/recordings';
import { dayLabel, timeLabel } from '../../lib/when';
import { useMe } from '../../lib/me';
import { Faces } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import { RecordCallButton, StartNowButton } from '../meetings/StartNow';
import './Recordings.css';

/**
 * THE RECORDINGS, at /recordings (Gordon's second point, 22 October):
 * attendees can reach their own, and an administrator has a place for all
 * of them. One list, one row per recording: what the call was about, when,
 * how long, who started it, where the transcript has got to, how long it
 * has left before the firm's retention takes it, and the faces of who was
 * there. Click a row for the player and the transcript.
 *
 * An administrator gets Mine | All and a search box; ?scope=all is what the
 * Advanced menu's "Call recordings" opens, so the address says which list
 * is on screen. Everybody else sees their own and nothing to switch.
 *
 * The server does the deciding about who may see what; this only asks. A
 * 503 naming migrate:065 means the code is deployed and the table is not,
 * and gets a calm panel rather than an error.
 */

/** What the call was about, as an icon: a ticket, a person, a group, a meeting (in the room, on another app, online). */
const aboutIcon = (about) => (about?.kind === 'ticket' ? 'ticket'
  : about?.kind === 'group' ? 'hash' : about?.kind === 'person' || about?.kind === 'thread' ? 'person'
    : about?.kind === 'meeting' ? (about.meeting_kind === 'call' ? 'share' : about.meeting_kind === 'room' ? 'home' : 'meeting') : 'clock');

/** A camera for video, a microphone for sound only. */
export const KindIcon = ({ kind, size = 16 }) => (kind === 'video' ? (
  <svg className="rc-kind-ic" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="1.75" y="4" width="9" height="8" rx="1.5" />
    <path d="M10.75 7l3.5-2v6l-3.5-2" />
  </svg>
) : (
  <svg className="rc-kind-ic" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <rect x="5.75" y="1.75" width="4.5" height="7.5" rx="2.25" />
    <path d="M3.5 7.5a4.5 4.5 0 0 0 9 0M8 12v2.25" />
  </svg>
));

/** "Transcribing…" as a chip whose colour says what kind of news it is. */
export const StateChip = ({ rec }) => {
  const words = transcriptWords(rec);
  const tone = /Ready/.test(words) ? 'good' : /Failed/.test(words) ? 'bad'
    : /Recording|Transcrib/.test(words) ? 'busy' : 'quiet';
  return <span className={`rc-chip is-${tone}`} data-state={tone}>{words}</span>;
};

export const ExpiryChip = ({ rec }) => {
  const e = expiryOf(rec);
  if (!e) return null;
  return <span className={`rc-chip is-${e.kind === 'kept' ? 'kept' : e.kind === 'soon' ? 'warn' : 'quiet'}`}>{e.text}</span>;
};

/** "Today 15:02", "Tue 20 Oct 09:30". */
export const whenOf = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${dayLabel(d)} ${timeLabel(d)}`;
};

/** The calm panel for a server that has the code and not the table yet. */
export function NotReadyPanel() {
  return (
    <div className="rc-notready" role="status">
      <h2>Call recordings are not switched on here yet</h2>
      <p>
        The server knows how to record calls but has not been given the table to keep them in.
        Whoever runs Jarvis needs to run <code>npm run migrate:065</code> on the server; nothing
        else is required, and nothing has been lost.
      </p>
    </div>
  );
}

export default function RecordingsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const admin = me?.isAdmin === true;
  // ?scope=all only means anything to an administrator; everybody else is on
  // "mine" whatever the address says, and the server would refuse otherwise.
  const scope = admin && params.get('scope') === 'all' ? 'all' : 'mine';
  const [q, setQ] = useState(params.get('q') || '');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [notThere, setNotThere] = useState(false);
  const latest = useRef(0);

  const load = useCallback(async (query) => {
    const n = ++latest.current;
    try {
      const list = await listRecordings({ scope, q: query });
      if (n !== latest.current) return;
      setRows(list);
      setError(null);
      setNotThere(false);
    } catch (err) {
      if (n !== latest.current) return;
      if (notReady(err)) { setNotThere(true); setRows([]); return; }
      // "all" refused: not an administrator after all. Back to mine, quietly.
      if (err.response?.status === 403 && scope === 'all') {
        setParams((p) => { const next = new URLSearchParams(p); next.delete('scope'); return next; }, { replace: true });
        return;
      }
      setError(reasonOf(err, 'Could not read the recordings.'));
      setRows((r) => r || []);
    }
  }, [scope, setParams]);

  useEffect(() => { load(q.trim()); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typing searches after a short pause rather than on every key.
  //
  // ONLY WHEN THE TEXT CHANGED. On the first render this effect also fired,
  // with the box still empty, and its 250ms timer carried the first render's
  // `load` - scope "mine". An administrator who pressed All inside that
  // window got their own list back a beat after everybody's had loaded: the
  // stale timer's request finished last and won. The mount's load is the
  // effect above; this one is for typing.
  const lastQ = useRef(q);
  useEffect(() => {
    if (!admin) return undefined;
    if (q === lastQ.current) return undefined;
    lastQ.current = q;
    const t = setTimeout(() => {
      load(q.trim());
      setParams((p) => {
        const next = new URLSearchParams(p);
        if (q.trim()) next.set('q', q.trim()); else next.delete('q');
        return next;
      }, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const setScope = (s) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (s === 'all') next.set('scope', 'all'); else next.delete('scope');
    return next;
  });

  // Newest first, whatever order they came in.
  const sorted = useMemo(() => (rows || []).slice().sort((a, b) =>
    (Date.parse(b.started_at || 0) || 0) - (Date.parse(a.started_at || 0) || 0)), [rows]);

  // Searching narrows locally as well: the server is asked, but the list on
  // screen should not wait for it to agree.
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return sorted;
    return sorted.filter((r) => [r.about?.label, r.started_by_name, ...(r.attendees || []).map((a) => a.name)]
      .filter(Boolean).some((s) => String(s).toLowerCase().includes(t)));
  }, [sorted, q]);

  const open = (rec) => navigate(`/recordings/${encodeURIComponent(rec.id)}`);

  return (
    <div className="rc-page">
      <header className="rc-head">
        <div className="rc-head-words">
          <h1 className="rc-title">{scope === 'all' ? 'Call recordings' : 'Your recordings'}</h1>
          <p className="rc-sub">
            {scope === 'all'
              ? 'Every call recorded in Jarvis, whoever was on it.'
              : 'Calls you were on that somebody recorded. Each has a transcript, and from the transcript a summary and to-dos.'}
          </p>
        </div>
        <div className="rc-head-actions">
          {/* A recording that is not of a Jarvis call (26 October): the
              meeting in the room, or a call on Teams, Zoom or the phone. Staff
              only, and only while Meetings and Recording are on for them. */}
          <StartNowButton onError={setError} compact />
          <RecordCallButton onError={setError} compact />
        </div>
        {admin && (
          <div className="rc-tools">
            <div className="rc-scope" role="tablist" aria-label="Whose recordings">
              <button type="button" role="tab" className="rc-scope-btn" aria-selected={scope === 'mine'}
                onClick={() => setScope('mine')}>Mine</button>
              <button type="button" role="tab" className="rc-scope-btn" aria-selected={scope === 'all'}
                onClick={() => setScope('all')}>All</button>
            </div>
            <label className="rc-search">
              <Icon name="search" size={15} />
              <input type="search" value={q} placeholder="Search by ticket, person or group"
                aria-label="Search recordings" onChange={(e) => setQ(e.target.value)} />
            </label>
          </div>
        )}
      </header>

      {notThere && <NotReadyPanel />}
      {error && <p className="rc-error" role="alert">{error}</p>}

      {rows === null && !notThere && <p className="rc-quiet">Loading…</p>}

      {rows !== null && !notThere && shown.length === 0 && !error && (
        <div className="rc-empty">
          <span className="rc-empty-ic" aria-hidden="true"><KindIcon kind="audio" size={22} /></span>
          <h2>{q.trim() ? 'Nothing matches that' : 'No recordings yet'}</h2>
          <p>
            {q.trim()
              ? 'Try a ticket number, a name or a group.'
              : 'During a call, press Record in the bar at the foot of the page. For a meeting in the room, or a call on Teams, Zoom or the phone, use the buttons above. Recordings land here when they end.'}
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <ul className="rc-list" aria-label="Recordings">
          {shown.map((rec) => {
            const about = rec.about || {};
            return (
              <li key={rec.id} className="rc-row" data-id={rec.id}>
                <button type="button" className="rc-row-btn" onClick={() => open(rec)}
                  aria-label={`Open the recording of ${about.label || 'the call'}`}>
                  <span className={`rc-row-kind is-${rec.kind === 'video' ? 'video' : 'audio'}`}
                    title={rec.kind === 'video' ? 'Audio and video' : 'Audio only'}>
                    <KindIcon kind={rec.kind} size={18} />
                  </span>
                  <span className="rc-row-main">
                    <span className="rc-row-about">
                      <Icon name={aboutIcon(about)} size={14} />
                      <span className="rc-row-label">{about.label || 'A call'}</span>
                    </span>
                    <span className="rc-row-meta">
                      <time dateTime={rec.started_at || undefined}>{whenOf(rec.started_at)}</time>
                      {rec.duration_ms > 0 && <><span className="rc-sep">·</span>{spokenLength(rec.duration_ms)}</>}
                      {rec.started_by_name && <><span className="rc-sep">·</span>started by {rec.started_by_name}</>}
                    </span>
                  </span>
                  <span className="rc-row-chips">
                    <StateChip rec={rec} />
                    <ExpiryChip rec={rec} />
                    {rec.has_summary && <span className="rc-chip is-quiet">Summary</span>}
                    {rec.has_todos && <span className="rc-chip is-quiet">To-dos</span>}
                  </span>
                  <span className="rc-row-faces">
                    <Faces members={rec.attendees || []} max={4} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {admin && scope === 'all' && (
        <p className="rc-foot">
          Retention is a firm setting: <Link to="/settings/communication/recording#retention">Settings → Recording &amp; AI → Keep recordings for</Link>.
          A recording can be kept past it from its own page.
        </p>
      )}
    </div>
  );
}
